import React, { useState } from 'react';

/* ─── Director Data ─── */
const directors = [
  {
    id: 'him-bahadur-sonar',
    name: 'Him Bahadur Sonar',
    role: 'Chairman & Managing Director',
    shortRole: 'CMD',
    photo: '/HB.jpg',
    accent: '#0d2d6b',
    accentLight: '#1a4a9e',
    tagline: 'Visionary leadership rooted in integrity and inclusive growth.',
    message: [
      'It is my privilege and honour to lead Shramjivi Goodshed Corporation Limited (SGCL), an organisation committed to transforming logistics, empowering the workforce, and strengthening India\'s railway goods shed operations.',
      'In today\'s dynamic world, efficiency, transparency, and innovation are the pillars of sustainable growth. At SGCL, we integrate advanced technology, robust infrastructure, and skilled manpower to deliver world-class services while ensuring safety, integrity, and accountability at every step.',
      'Our greatest strength is our people. We believe in their welfare, skill development, and in creating equal opportunities for their growth and success.',
      'Together with our dedicated team and partners, we will continue to set new benchmarks, build lasting relationships, and contribute meaningfully to India\'s economic progress.',
      'I extend my sincere gratitude to our employees, customers, partners, and well-wishers for their trust and support.',
      'Together, let us build a stronger, more inclusive and prosperous India.',
    ],
    closing: 'Warm Regards,\nHim Bahadur Sonar\nChairman & Managing Director\nShramjivi Goodshed Corporation Limited',
  },
  {
    id: 'kokil-kantha-gogoi',
    name: 'Kokil Kantha Gogoi',
    role: 'Chief Executive Director',
    shortRole: 'CED',
    photo: '/KKG.jpg',
    accent: '#92400e',
    accentLight: '#b45309',
    tagline: 'Building trust, opportunities, and a stronger tomorrow.',
    message: [
      'At Shramjivi Goodshed Corporation Limited, we are building more than just infrastructure — we are building trust, opportunities, and a stronger tomorrow.',
      'Our purpose is to bring efficiency, transparency, and innovation to every aspect of goods shed operations and logistics services. We combine technology, professional management, and a people-first approach to deliver value to our customers, partners, and the nation.',
      'I believe that our greatest asset is our people. Their dedication, hard work, and commitment drive our progress and inspire us to achieve higher every day.',
      'As we move forward, we remain focused on safety, sustainability, operational excellence, and social responsibility. Together, we aim to set new benchmarks in the logistics sector and contribute meaningfully to India\'s growth story.',
      'I thank all our stakeholders for their trust and support. Let us continue this journey together towards a future of shared success and national progress.',
    ],
    closing: 'Warm Regards,\nKokil Kantha Gogoi\nChief Executive Director\nShramjivi Goodshed Corporation Limited',
  },
];

/* ─── Departments ─── */
const departments = [
  { icon: '⚙️', name: 'Operations & Logistics', head: 'Operations Manager', color: '#1e3a8a', gradient: 'from-[#1e3a8a] to-[#1d4ed8]', responsibilities: ['Goods shed workforce deployment & supervision','Loading / unloading operations oversight','Freight movement coordination','Shift scheduling & productivity monitoring'] },
  { icon: '💼', name: 'Finance & Accounts', head: 'Chief Finance Officer', color: '#064e3b', gradient: 'from-[#064e3b] to-[#047857]', responsibilities: ['Budget planning & financial reporting','Payroll processing & disbursement','MCA statutory compliances & filings','Audit coordination & internal controls'] },
  { icon: '👥', name: 'Human Resources', head: 'HR Manager', color: '#7c2d12', gradient: 'from-[#7c2d12] to-[#c2410c]', responsibilities: ['Worker registration & onboarding','EPFO / ESIC enrolment & compliance','Training, skill development & certification','Grievance redressal & welfare coordination'] },
  { icon: '📜', name: 'Legal & Compliance', head: 'Compliance Officer', color: '#3b0764', gradient: 'from-[#3b0764] to-[#6d28d9]', responsibilities: ['Labour law & statutory compliance','MCA filings & ROC correspondence','Contract management & legal reviews','Regulatory liaison with government bodies'] },
  { icon: '💻', name: 'Information Technology', head: 'IT In-charge', color: '#0c4a6e', gradient: 'from-[#0c4a6e] to-[#0369a1]', responsibilities: ['Digital attendance & QR monitoring systems','Portal development & maintenance','Data security & system infrastructure','Real-time reporting dashboards'] },
  { icon: '📣', name: 'Public Relations & BD', head: 'PR & BD Manager', color: '#78350f', gradient: 'from-[#78350f] to-[#b45309]', responsibilities: ['Stakeholder communications & outreach','Railway authority & govt. liaison','New business development & partnerships','Brand identity & public communications'] },
];

/* ─── Governance Pillars ─── */
const pillars = [
  { icon: '🔍', title: 'Transparency', desc: 'Every decision, transaction and operation is conducted openly with full accountability to all stakeholders.' },
  { icon: '⚖️', title: 'Integrity', desc: 'We uphold the highest ethical standards across all functions — from recruitment to regulatory compliance.' },
  { icon: '🏛️', title: 'Accountability', desc: 'Each department and officer is held responsible for their deliverables and performance outcomes.' },
  { icon: '🌱', title: 'Sustainability', desc: 'Our governance framework is built for long-term impact — for workers, railways, and the nation.' },
];

/* ─────────────────────────────────────────────── */
/* ─── Director Detail Page ─── */
/* ─────────────────────────────────────────────── */
const DirectorDetail = ({ director, onBack }) => {
  return (
    <div className="bg-slate-50 min-h-screen font-sans pb-24">
      {/* Hero Banner */}
      <div className="relative text-white overflow-hidden" style={{ minHeight: '420px' }}>
        <img src="/g2.png" alt={director.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d2d6b] via-[#0d2d6b]/85 to-[#0d2d6b]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001529]/80 via-transparent to-transparent" />
        <div className="absolute top-0 right-0 opacity-5 text-[220px] font-black select-none leading-none -mr-10 -mt-10">SGCL</div>
        <div className="relative z-10 px-4 md:px-20 py-20 max-w-5xl">
          <span className="bg-govt-orange text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">Director's Message</span>
          <h1 className="text-4xl md:text-6xl font-black mt-4 mb-5 leading-tight uppercase italic">{director.name}</h1>
          <p className="text-lg md:text-xl text-blue-100 font-bold border-l-4 border-govt-orange pl-6 italic max-w-2xl leading-relaxed">"{director.tagline}"</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-[#FF9933]" /><div className="flex-1 bg-white" /><div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      {/* Message Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 -mt-12 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Profile Sidebar */}
          <div className="lg:col-span-4 bg-[#001529] text-white rounded-3xl overflow-hidden shadow-xl border border-white/10">
            <div className="h-2 w-full" style={{ backgroundColor: director.accentLight }} />
            <div className="p-8 text-center">
              <div className="relative mx-auto w-44 h-52 rounded-2xl overflow-hidden shadow-lg border-2 border-white/10 mb-5">
                <img src={director.photo} alt={director.name} className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
              <h2 className="text-xl font-black uppercase tracking-wider mb-1">{director.name}</h2>
              <p className="text-[10px] font-black uppercase tracking-widest mb-6" style={{ color: director.accentLight }}>{director.role}</p>
              <div className="space-y-3 text-left border-t border-white/10 pt-5">
                <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Designation</span>
                  <span className="text-xs font-bold text-white uppercase">{director.shortRole} — SGCL</span>
                </div>
                <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">MCA Status</span>
                  <span className="text-xs font-bold text-white uppercase flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Verified Director
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Statement */}
          <div className="lg:col-span-8 bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
            <div className="px-8 py-5 border-b border-slate-100 flex items-center gap-3 bg-slate-50/50">
              <div className="w-1 h-8 rounded-full" style={{ backgroundColor: director.accentLight }} />
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Official Communication</p>
                <h3 className="font-black text-slate-800 text-sm uppercase tracking-wider">Director's Message</h3>
              </div>
            </div>
            <div className="p-8 md:p-10 space-y-6 text-slate-600 text-[15px] leading-8 font-medium text-justify">
              <p className="font-semibold text-slate-700">
                <span className="float-left text-6xl font-black mr-3 mt-1.5 leading-none" style={{ color: director.accentLight }}>{director.message[0][0]}</span>
                {director.message[0].slice(1)}
              </p>
              {director.message.slice(1).map((para, idx) => <p key={idx}>{para}</p>)}
            </div>
            <div className="m-8 md:m-10 p-6 rounded-2xl border" style={{ background: `${director.accent}04`, borderColor: `${director.accent}15` }}>
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <p className="text-xs text-slate-500 font-semibold">This message is digitally verified for corporate publication.</p>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-slate-400 italic">Warm Regards,</p>
                  <p className="text-2xl font-black italic mt-1 font-serif" style={{ color: director.accentLight }}>{director.name}</p>
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-1">{director.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 text-center">
          <button onClick={onBack} className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest text-white shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl bg-govt-blue group">
            <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to Management
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────── */
/* ─── Management Main Page ─── */
/* ─────────────────────────────────────────────── */
const Management = () => {
  const [selectedDirector, setSelectedDirector] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  if (selectedDirector) {
    return <DirectorDetail director={selectedDirector} onBack={() => setSelectedDirector(null)} />;
  }

  const tabs = [
    { id: 0, label: 'Board of Directors', icon: '👥' },
    { id: 1, label: 'Org Structure', icon: '🏛️' },
    { id: 2, label: 'Departments', icon: '💼' },
    { id: 3, label: 'Governance', icon: '⚖️' },
  ];

  return (
    <div className="bg-slate-100 min-h-screen font-sans pb-24">

      {/* ── HERO ── */}
      <div className="relative text-white overflow-hidden" style={{ minHeight: '420px' }}>
        <img src="/g2.png" alt="SGCL Management" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d2d6b] via-[#0d2d6b]/85 to-[#0d2d6b]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001529]/80 via-transparent to-transparent" />
        <div className="absolute top-0 right-0 opacity-5 text-[220px] font-black select-none leading-none -mr-10 -mt-10">SGCL</div>
        <div className="relative z-10 px-4 md:px-20 py-20 max-w-5xl">
          <span className="bg-govt-orange text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">Corporate Governance</span>
          <h1 className="text-4xl md:text-6xl font-black mt-4 mb-6 leading-tight uppercase italic">
            Management &amp; <span className="text-govt-orange">Governance</span>
          </h1>
          <p className="text-lg md:text-xl text-blue-100 font-bold border-l-4 border-govt-orange pl-6 italic max-w-2xl leading-relaxed mb-8">
            A structured, transparent, and people-first management framework driving Shramjivi Goodshed Corporation Limited towards operational excellence and national progress.
          </p>
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-5 py-3">
            <span className="text-govt-orange text-lg">⚖️</span>
            <div>
              <p className="text-white/60 text-[9px] font-black uppercase tracking-widest">MCA Registration Status</p>
              <p className="text-white font-black font-mono text-sm tracking-wide">ACTIVE / REGISTERED</p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-[#FF9933]" /><div className="flex-1 bg-white" /><div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      {/* ── STATS STRIP ── */}
      <div className="bg-govt-blue text-white shadow-xl">
        <div className="container mx-auto px-4 md:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {[{ value: '2', label: 'Board Directors' }, { value: '6', label: 'Departments' }, { value: 'MCA', label: 'Registered Under' }, { value: '100%', label: 'Compliance Rate' }].map((s, i) => (
              <div key={i} className="py-6 px-4 text-center hover:bg-white/5 transition-colors">
                <p className="text-2xl md:text-3xl font-black text-govt-orange">{s.value}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-blue-200 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── TAB NAVIGATION (Full width, outside the card) ── */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-30">
        <div className="container mx-auto px-4 md:px-20">
          <div className="flex overflow-x-auto no-scrollbar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2.5 px-8 py-5 font-black text-xs uppercase tracking-widest whitespace-nowrap border-b-4 transition-all duration-200 flex-shrink-0
                  ${activeTab === tab.id
                    ? 'border-govt-orange text-govt-blue bg-orange-50/50'
                    : 'border-transparent text-slate-400 hover:text-govt-blue hover:bg-slate-50'
                  }`}
              >
                <span className="text-base">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── TAB CONTENT AREA ── */}
      <div className="container mx-auto px-4 md:px-20 py-12">

        {/* ── TAB 0: BOARD OF DIRECTORS ── */}
        {activeTab === 0 && (
          <div>
            <div className="mb-10">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Apex Leadership</p>
              <h2 className="text-2xl md:text-3xl font-black text-govt-blue uppercase italic">Board of Directors</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {directors.map((director) => (
                <div
                  key={director.id}
                  onClick={() => setSelectedDirector(director)}
                  className="group cursor-pointer bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-400 border border-slate-100 hover:-translate-y-1 flex flex-col md:flex-row"
                >
                  {/* Photo Panel */}
                  <div
                    className="md:w-64 shrink-0 relative overflow-hidden"
                    style={{ background: `linear-gradient(160deg, ${director.accent} 0%, #001529 100%)` }}
                  >
                    <img
                      src={director.photo}
                      alt={director.name}
                      className="w-full md:w-full h-64 md:h-full object-cover object-center opacity-90 group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest text-white" style={{ backgroundColor: director.accentLight }}>
                        {director.shortRole}
                      </span>
                    </div>
                  </div>

                  {/* Content Panel */}
                  <div className="flex-1 p-7 flex flex-col justify-between">
                    <div>
                      <div className="mb-4">
                        <h3 className="text-xl font-black text-slate-900 uppercase leading-tight tracking-wide">{director.name}</h3>
                        <p className="text-xs font-black uppercase tracking-widest mt-1" style={{ color: director.accentLight }}>{director.role}</p>
                      </div>

                      <div className="relative p-4 rounded-xl mb-4 bg-slate-50 border-l-4" style={{ borderColor: director.accentLight }}>
                        <span className="absolute right-3 top-1 text-3xl text-slate-200 font-serif select-none">"</span>
                        <p className="text-slate-600 text-sm font-semibold italic leading-relaxed pr-5">"{director.tagline}"</p>
                      </div>
                      <p className="text-slate-400 text-xs font-medium leading-relaxed line-clamp-3">{director.message[0]}</p>
                    </div>
                    <div className="flex items-center justify-between pt-5 border-t border-slate-100 mt-5">
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">Tap to read full message</span>
                      <div
                        className="inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest px-4 py-2.5 rounded-xl text-white shadow group-hover:shadow-md group-hover:translate-x-1 transition-all"
                        style={{ backgroundColor: director.accentLight }}
                      >
                        Read Message →
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── TAB 1: ORG STRUCTURE ── */}
        {activeTab === 1 && (
          <div>
            <div className="mb-10">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Corporate Hierarchy</p>
              <h2 className="text-2xl md:text-3xl font-black text-govt-blue uppercase italic">Management Structure</h2>
            </div>

            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 md:p-12">
              <div className="flex flex-col items-center">

                {/* Board */}
                <div className="w-full max-w-sm bg-govt-blue text-white rounded-2xl p-5 text-center shadow-lg border-b-4 border-govt-orange relative overflow-hidden">
                  <div className="absolute right-0 top-0 opacity-5 text-8xl font-black leading-none -mr-2 -mt-2 select-none">SGCL</div>
                  <p className="text-govt-orange text-[9px] font-black uppercase tracking-widest mb-1">Apex Authority</p>
                  <h3 className="text-xl font-black uppercase italic">Board of Directors</h3>
                  <p className="text-blue-200 text-[9px] font-bold uppercase tracking-widest mt-0.5">Shramjivi Goodshed Corporation Limited</p>
                </div>

                {/* Connector */}
                <div className="flex flex-col items-center py-2">
                  <div className="w-0.5 h-8 bg-slate-300" />
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                  <div className="w-0.5 h-4 bg-slate-300" />
                </div>

                {/* Executive Row */}
                <div className="relative w-full max-w-2xl">
                  <div className="absolute top-0 left-1/4 right-1/4 h-0.5 bg-slate-300" />
                  <div className="flex justify-around w-full">
                    {directors.map((d, i) => (
                      <div key={i} className="flex flex-col items-center w-1/2">
                        <div className="w-0.5 h-4 bg-slate-300" />
                        <div
                          onClick={() => setSelectedDirector(d)}
                          className="flex flex-col items-center justify-center bg-white border-2 rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg w-[90%] max-w-[250px] text-center"
                          style={{ borderColor: d.accentLight }}
                        >
                          <div className="text-center">
                            <p className="text-[10px] font-black uppercase tracking-wider text-slate-800 leading-tight">{d.role}</p>
                            <p className="text-[9px] text-slate-400 font-semibold italic mt-2">Click to read →</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Connector down to departments */}
                <div className="flex flex-col items-center py-2">
                  <div className="w-0.5 h-8 bg-slate-300" />
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                  <div className="w-0.5 h-4 bg-slate-300" />
                </div>

                {/* Departments Bar */}
                <div className="w-full">
                  <p className="text-center text-[9px] font-black uppercase tracking-widest text-slate-400 mb-6">6 Functional Departments</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {departments.map((d, i) => (
                      <div key={i} className={`rounded-2xl p-4 text-white text-center bg-gradient-to-br ${d.gradient} shadow-md hover:scale-105 transition-transform cursor-default`}>
                        <div className="text-2xl mb-2">{d.icon}</div>
                        <p className="text-[9px] font-black uppercase leading-tight">{d.name}</p>
                        <p className="text-[8px] text-white/60 mt-1">{d.head}</p>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* ── TAB 2: DEPARTMENTS ── */}
        {activeTab === 2 && (
          <div>
            <div className="mb-10">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Internal Operations</p>
              <h2 className="text-2xl md:text-3xl font-black text-govt-blue uppercase italic">Functional Departments</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {departments.map((dept, i) => (
                <div key={i} className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 border border-slate-100">
                  {/* Gradient Header */}
                  <div className={`bg-gradient-to-r ${dept.gradient} p-6 relative overflow-hidden`}>
                    <div className="absolute right-4 top-2 text-6xl font-black text-white/10 select-none">{`0${i + 1}`}</div>
                    <div className="flex items-center gap-4 relative z-10">
                      <div className="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center text-3xl shadow-inner">
                        {dept.icon}
                      </div>
                      <div>
                        <h3 className="font-black text-white text-sm uppercase leading-tight tracking-wide">{dept.name}</h3>
                        <p className="text-white/70 text-[9px] font-black uppercase tracking-widest mt-1">{dept.head}</p>
                      </div>
                    </div>
                  </div>
                  {/* Responsibilities */}
                  <div className="p-6">
                    <ul className="space-y-3">
                      {dept.responsibilities.map((r, j) => (
                        <li key={j} className="flex items-start gap-3 text-sm text-slate-600 font-medium leading-relaxed">
                          <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ backgroundColor: dept.color }} />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── TAB 3: GOVERNANCE ── */}
        {activeTab === 3 && (
          <div className="space-y-12">
            {/* Pillars section — dark full-width panel */}
            <div>
              <div className="mb-8">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Foundational Principles</p>
                <h2 className="text-2xl md:text-3xl font-black text-govt-blue uppercase italic">Corporate Governance Pillars</h2>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-xl relative">
                <img src="/g1.png" alt="Governance" className="absolute inset-0 w-full h-full object-cover opacity-20" />
                <div className="absolute inset-0 bg-[#001529]/90" />
                <div className="relative z-10 p-8 md:p-12">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {pillars.map((p, i) => (
                      <div key={i} className="group bg-white/5 backdrop-blur-sm border border-white/10 hover:border-govt-orange/40 hover:bg-white/10 rounded-3xl p-7 transition-all duration-300 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-white/10 group-hover:bg-govt-orange/20 flex items-center justify-center text-3xl mx-auto mb-5 transition-colors duration-300 border border-white/10">
                          {p.icon}
                        </div>
                        <h3 className="font-black text-white text-sm uppercase mb-3 tracking-wider">{p.title}</h3>
                        <p className="text-blue-200/80 text-xs font-semibold leading-relaxed">{p.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Management Approach + Commitments */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Timeline Philosophy */}
              <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8">
                <div className="mb-6">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Leadership Philosophy</p>
                  <h3 className="text-xl font-black text-govt-blue uppercase italic">Our Approach</h3>
                </div>
                <div className="space-y-7 relative">
                  <div className="absolute left-5 top-1 bottom-1 w-0.5 bg-slate-100" />
                  {[
                    { num: '01', title: 'People-First Leadership', desc: 'Every policy, process and decision at SGCL begins with the welfare of our workers. A cared-for workforce is our most productive asset.' },
                    { num: '02', title: 'Technology-Driven Operations', desc: 'We leverage QR attendance, live dashboards, and data analytics to bring real-time visibility and accountability to all operations.' },
                    { num: '03', title: 'Compliance & Ethics', desc: 'SGCL maintains zero tolerance for non-compliance. All obligations under MCA, Labour Laws, EPFO, and ESIC are met with precision.' },
                    { num: '04', title: 'Continuous Improvement', desc: 'Our management review system encourages feedback, innovation and iteration — constantly refining processes across every goods shed.' },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-5 items-start group relative z-10">
                      <div className="w-10 h-10 rounded-xl bg-govt-blue group-hover:bg-govt-orange text-white font-black text-xs flex items-center justify-center shrink-0 transition-colors shadow-md">
                        {item.num}
                      </div>
                      <div className="pt-1">
                        <h4 className="font-black text-govt-blue text-sm uppercase mb-1">{item.title}</h4>
                        <p className="text-slate-500 text-xs font-semibold leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Art image to fill space */}
                <div className="mt-8 rounded-2xl overflow-hidden border border-slate-100 shadow-md">
                  <img src="/art.png" alt="SGCL Operations" className="w-full h-48 object-cover object-center" />
                </div>
              </div>


              {/* Commitments + Image */}
              <div className="flex flex-col gap-6">
                <div className="relative rounded-3xl overflow-hidden shadow-xl flex-1" style={{ minHeight: '200px' }}>
                  <img src="/g3.png" alt="Management in action" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#001529] via-[#001529]/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-7">
                    <p className="text-govt-orange text-[9px] font-black uppercase tracking-widest mb-1">Management in Action</p>
                    <p className="text-white text-lg font-black italic leading-snug">"Every operation, every day, every worker — managed with care."</p>
                  </div>
                </div>
                <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-7">
                  <h4 className="font-black text-govt-blue text-sm uppercase mb-5 tracking-wide pb-3 border-b border-slate-100">Management Commitments</h4>
                  <div className="space-y-4">
                    {['Timely disbursement of wages and benefits', 'Zero tolerance for exploitation or misconduct', 'Regular training and upskilling for all workers', 'Open-door policy for grievances and feedback', 'Full statutory compliance — EPFO, ESIC, MCA'].map((c, i) => (
                      <div key={i} className="flex items-center gap-3.5 group">
                        <div className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center shrink-0 border border-green-200 group-hover:bg-green-100 transition-colors">
                          <span className="text-green-700 text-[9px] font-black">✓</span>
                        </div>
                        <p className="text-slate-600 text-sm font-semibold group-hover:text-slate-800 transition-colors">{c}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Quote */}
            <div className="bg-govt-blue rounded-3xl p-10 md:p-14 text-center shadow-2xl border-b-8 border-govt-orange relative overflow-hidden">
              <div className="absolute top-0 right-0 opacity-[0.03] text-[180px] font-black select-none leading-none -mr-8 -mt-8">SGCL</div>
              <span className="text-6xl opacity-20 font-black leading-none block mb-4 text-govt-orange">"</span>
              <p className="text-white text-lg md:text-xl font-bold italic max-w-3xl mx-auto leading-relaxed relative z-10">
                The management remains dedicated to supporting Indian Railways in achieving higher freight efficiency, improved service standards, and long-term operational sustainability.
              </p>
              <p className="text-blue-300 text-xs font-black uppercase tracking-widest mt-6 relative z-10">— Board of Directors, Shramjivi Goodshed Corporation Limited</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Management;
